import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';

export interface AdSet {
  id: string;
  workspace_id: string;
  campaign_id: string;
  ad_account_id: string;
  fb_adset_id: string | null;
  name: string;
  status: string;
  effective_status: string | null;
  status_stale_since: string | null;
  daily_budget: number | null;
  lifetime_budget: number | null;
  bid_amount: number | null;
  bid_strategy: string | null;
  billing_event: string;
  optimization_goal: string;
  targeting: Record<string, unknown>;
  promoted_object?: { pixel_id?: string; custom_event_type?: string; application_id?: string; object_store_url?: string; page_id?: string };
  attribution_spec?: Array<{ event_type: string; window_days: number }>;
  destination_type?: string;
  start_time: string | null;
  end_time: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  campaign?: {
    id: string;
    name: string;
  };
  ad_account?: {
    id: string;
    name: string;
  };
}

export interface AdSetFilters {
  campaign_id?: string;
  ad_account_id?: string;
  status?: string | string[];
  search?: string;
  offset?: number;
  limit?: number;
}

export interface QueryResult<T> {
  data: T[];
  total: number;
}

function buildWhereCondition(filters: AdSetFilters): WhereCondition | undefined {
  const conditions: WhereCondition[] = [];

  if (filters.campaign_id) {
    conditions.push({ adset: { campaign_id: filters.campaign_id } });
  }

  if (filters.ad_account_id) {
    conditions.push({ adset: { ad_account_id: filters.ad_account_id } });
  }

  if (filters.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    conditions.push({ adset: { status: { op: 'in', value: statuses } } });
  }

  if (filters.search) {
    conditions.push({ adset: { name: { op: 'ilike', value: `%${filters.search}%` } } });
  }

  return conditions.length > 0 ? { $and: conditions } : undefined;
}

export const adsetsService = {
  query: async (filters: AdSetFilters = {}): Promise<QueryResult<AdSet>> => {
    const payload: QueryPayload = {
      where: buildWhereCondition(filters),
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 25,
      order_by: 'adset.created_at',
      order: 'desc',
    };

    const response = await api.post<QueryResponse<AdSet>>('/adsets/query', payload);
    return {
      data: response.data.data,
      total: response.data._meta?.total ?? 0,
    };
  },

  getById: async (id: string): Promise<AdSet> => {
    const response = await api.get<ApiResponse<AdSet>>(`/adsets/${id}`);
    return response.data.data!;
  },

  create: async (data: Partial<AdSet>): Promise<AdSet> => {
    const response = await api.post<ApiResponse<AdSet>>('/adsets', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<AdSet>): Promise<AdSet> => {
    const response = await api.patch<ApiResponse<AdSet>>(`/adsets/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/adsets/${id}`);
  },

  duplicate: async (id: string, data?: { name?: string; campaign_id?: string }): Promise<AdSet> => {
    const response = await api.post<ApiResponse<AdSet>>(`/adsets/${id}/duplicate`, data);
    return response.data.data!;
  },

};
